/**
 * FleetPage — company vehicle list + add vehicle + BP3 fleet setup:
 * CSV bulk import (preview/commit, trailers exempt from vehicle quota),
 * trailers & attached units, driver designation + vehicle assignment.
 * Uses dotApi (POST /dot/vehicles — quota-gated + FLEET keys server-side).
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Truck, Plus, X, Upload, UserCog } from 'lucide-react';
import { dotApi, importApi, authApi } from '../utils/api';
import { friendlyQuotaError } from '../utils/quotaError';
import CsvImportPanel, { canUseCsvImport } from '../components/CsvImportPanel';

const VEHICLE_TYPES = [
  { value: 'PICKUP', label: 'Pickup' },
  { value: 'CREW_CAB_OVER', label: 'Crew Cab' },
  { value: 'BUCKET_TRUCK', label: 'Bucket Truck' },
  { value: 'DIGGER_DERRICK', label: 'Digger Derrick' },
  { value: 'CRANE_CARRIER', label: 'Crane Carrier' },
  { value: 'DUMP', label: 'Dump' },
  { value: 'OTHER', label: 'Other' },
] as const;

const ATTACHED_UNIT_TYPES = [
  { value: 'TRAILER', label: 'Trailer' },
  { value: 'DOLLY', label: 'Dolly' },
  { value: 'TOWED_EQUIP', label: 'Towed Equipment' },
  { value: 'OTHER', label: 'Other' },
] as const;

const CDL_CLASSES = ['A', 'B', 'C', 'NONE'] as const;

const STATUS_STYLES: Record<string, { label: string; cls: string }> = {
  AVAILABLE: { label: 'Available', cls: 'bg-green-100 text-green-700' },
  IN_SERVICE: { label: 'In Service', cls: 'bg-blue-100 text-blue-700' },
  RETIRED: { label: 'Retired', cls: 'bg-gray-100 text-gray-500' },
};

// BP3: fleet import template. kind=VEHICLE rows go through the quota-gated
// vehicle path; kind=TRAILER rows become attached units and NEVER consume
// the vehicle quota. attachedTo must match a vehicle unit number in your
// fleet or earlier in the file.
const FLEET_IMPORT_TEMPLATE = [
  // BP3 audit M3: TRAILER rows may only attach to a vehicle that already
  // exists in the fleet or appears in an EARLIER row — trailers must come
  // AFTER their owning vehicle row.
  'unitNumber,vehicleType,vin,plate,gvwrLb,odometerMi,kind,unitType,attachedTo,label',
  'T-100,PICKUP,1FTBF2A69EEB12345,ABC-123,,45000,VEHICLE,,,',
  'TR-9,,,,,,TRAILER,TRAILER,T-100,Tool trailer',
].join('\n');

export default function FleetPage() {
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({ unitNumber: '', vehicleType: 'PICKUP', vin: '', plate: '', odometerMi: '' });

  // BP3: import + trailers/attached units + drivers & assignment
  const [showImport, setShowImport] = useState(false);
  // BP3 audit L4: import UI is gated to ADMIN/SUPER_ADMIN (server routes are
  // requireRole fail-closed; this only hides the entry point).
  const canImport = canUseCsvImport();
  const [units, setUnits] = useState<any[]>([]);
  const [configs, setConfigs] = useState<any[]>([]);
  const [drivers, setDrivers] = useState<any[]>([]);
  const [teamUsers, setTeamUsers] = useState<any[]>([]);
  const [showTrailerForm, setShowTrailerForm] = useState(false);
  const [trailerForm, setTrailerForm] = useState({ unitNumber: '', unitType: 'TRAILER', attachedTo: '', label: '' });
  const [driverForm, setDriverForm] = useState({ userId: '', cdlClass: 'NONE' });
  const [assignForm, setAssignForm] = useState({ vehicleId: '', driverId: '', shiftDate: '' });

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      dotApi.listVehicles(),
      dotApi.listAttachedUnits(),
      dotApi.listVehicleConfigs(),
      dotApi.listDrivers(),
      authApi.listCompanyUsers().catch(() => null),
    ])
      .then(([v, u, c, d, users]: any[]) => {
        setVehicles(v.data ?? v ?? []);
        setUnits(u.data ?? u ?? []);
        setConfigs(c.data ?? c ?? []);
        setDrivers(d.data ?? d ?? []);
        const ulist = users?.data?.data ?? users?.data ?? users;
        setTeamUsers(Array.isArray(ulist) ? ulist : []);
      })
      .catch((e: any) => setError(e?.response?.data?.message || 'Could not load vehicles.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const addVehicle = async () => {
    setSaving(true); setError(''); setSuccess('');
    try {
      const payload: any = { unitNumber: form.unitNumber, vehicleType: form.vehicleType };
      if (form.vin) payload.vin = form.vin;
      if (form.plate) payload.plate = form.plate;
      if (form.odometerMi) payload.odometerMi = Number(form.odometerMi);
      await dotApi.createVehicle(payload);
      setSuccess(`Vehicle ${form.unitNumber} added`);
      setForm({ unitNumber: '', vehicleType: 'PICKUP', vin: '', plate: '', odometerMi: '' });
      setShowAdd(false);
      load();
    } catch (e: any) {
      // QUOTA-MED-01: shared quota parser — flat vehicle body AND legacy
      // JSON-in-message both render the friendly why+how message (the old
      // fallback showed raw JSON for legacy bodies and dropped counts).
      setError(friendlyQuotaError(e, 'Could not add this vehicle.'));
    } finally {
      setSaving(false);
    }
  };

  const addTrailer = async () => {
    setSaving(true); setError(''); setSuccess('');
    try {
      await dotApi.createAttachedUnit({
        unitNumber: trailerForm.unitNumber,
        unitType: trailerForm.unitType,
        label: trailerForm.label || undefined,
        axleSpec: [{ position: 'T1', duals: false }],
        ownerId: Number(trailerForm.attachedTo),
      });
      setSuccess(`Attached unit ${trailerForm.unitNumber} added (does not count against the vehicle quota)`);
      setTrailerForm({ unitNumber: '', unitType: 'TRAILER', attachedTo: '', label: '' });
      setShowTrailerForm(false);
      load();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Could not add this attached unit.');
    } finally {
      setSaving(false);
    }
  };

  const designateDriver = async () => {
    setSaving(true); setError(''); setSuccess('');
    try {
      await dotApi.upsertDriver({ userId: Number(driverForm.userId), cdlClass: driverForm.cdlClass });
      setSuccess('Driver designated.');
      setDriverForm({ userId: '', cdlClass: 'NONE' });
      load();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Could not designate this driver.');
    } finally {
      setSaving(false);
    }
  };

  const createAssignment = async () => {
    setSaving(true); setError(''); setSuccess('');
    try {
      await dotApi.createAssignment({ vehicleId: Number(assignForm.vehicleId), driverId: Number(assignForm.driverId), shiftDate: assignForm.shiftDate });
      setSuccess('Vehicle assigned to driver.');
      setAssignForm({ vehicleId: '', driverId: '', shiftDate: '' });
      load();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Could not create this assignment.');
    } finally {
      setSaving(false);
    }
  };

  const userName = (userId: number) => {
    const u = teamUsers.find((x) => x.id === userId);
    return u ? `${u.firstName} ${u.lastName}` : `User #${userId}`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-6">
          <ArrowLeft className="h-4 w-4" /> Back to dashboard
        </Link>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-full bg-cyan-100 flex items-center justify-center">
              <Truck className="h-6 w-6 text-cyan-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Fleet</h1>
              <p className="text-sm text-gray-500">{vehicles.length} {vehicles.length === 1 ? 'vehicle' : 'vehicles'} · {units.length} attached {units.length === 1 ? 'unit' : 'units'}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {canImport ? (
              <button
                onClick={() => { setShowImport(!showImport); setError(''); setSuccess(''); }}
                data-testid="toggle-fleet-import"
                className="inline-flex items-center gap-2 px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900"
              >
                <Upload className="h-4 w-4" /> Import CSV
              </button>
            ) : (
              <span
                title="CSV import is available to admins only."
                className="inline-flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-500 rounded-lg cursor-not-allowed"
                data-testid="fleet-import-unavailable"
              >
                <Upload className="h-4 w-4" /> Import CSV (admins only)
              </span>
            )}
            <button
              onClick={() => { setShowAdd(!showAdd); setShowImport(false); setError(''); setSuccess(''); }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700"
            >
              {showAdd ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
              {showAdd ? 'Close' : 'Add vehicle'}
            </button>
          </div>
        </div>

        {success && <div className="mb-4 p-3 rounded-lg bg-green-50 text-green-700 text-sm">{success}</div>}
        {error && !showAdd && <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>}

        {canImport && showImport && (
          <CsvImportPanel
            title="Import your fleet from CSV"
            template={FLEET_IMPORT_TEMPLATE}
            columns={['unitNumber', 'vehicleType', 'vin', 'plate', 'odometerMi', 'kind', 'unitType', 'attachedTo']}
            previewFn={importApi.fleetPreview}
            commitFn={importApi.fleetCommit}
            onDone={load}
          />
        )}

        {showAdd && (
          <div className="mb-6 bg-white rounded-xl shadow border border-gray-100 p-6">
            <h2 className="font-semibold text-gray-900 mb-4">Add a vehicle</h2>
            {error && <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Unit number *</label>
                <input value={form.unitNumber} onChange={(e) => setForm({ ...form, unitNumber: e.target.value })}
                  placeholder="e.g. T-100"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
                <select value={form.vehicleType} onChange={(e) => setForm({ ...form, vehicleType: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                  {VEHICLE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">VIN</label>
                <input value={form.vin} onChange={(e) => setForm({ ...form, vin: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Plate</label>
                <input value={form.plate} onChange={(e) => setForm({ ...form, plate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Odometer (miles)</label>
                <input type="number" value={form.odometerMi} onChange={(e) => setForm({ ...form, odometerMi: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
            </div>
            <button
              onClick={addVehicle}
              disabled={saving || !form.unitNumber.trim()}
              className="mt-4 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50"
            >
              {saving ? 'Adding…' : 'Add vehicle'}
            </button>
          </div>
        )}

        <div className="bg-white rounded-xl shadow border border-gray-100 divide-y divide-gray-100">
          {loading ? (
            <div className="p-6 text-sm text-gray-500">Loading…</div>
          ) : vehicles.length === 0 ? (
            <div className="p-6 text-sm text-gray-500">No vehicles yet. Import a CSV above or add your first one.</div>
          ) : (
            vehicles.map((v) => {
              const st = STATUS_STYLES[v.status] ?? STATUS_STYLES.AVAILABLE;
              const cfg = configs.find((c: any) => c.vehicleId === v.id);
              return (
                <div key={v.id} className="flex items-center justify-between px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-lg bg-cyan-50 flex items-center justify-center">
                      <Truck className="h-5 w-5 text-cyan-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 text-sm">{v.unitNumber}</p>
                      <p className="text-xs text-gray-500">
                        {VEHICLE_TYPES.find((t) => t.value === v.vehicleType)?.label ?? v.vehicleType}
                        {v.vin ? ` · VIN ${v.vin}` : ''}{v.odometerMi ? ` · ${v.odometerMi.toLocaleString()} mi` : ''}
                        {cfg ? ` · ${cfg.axleSpec?.length ?? 0}-axle config` : ''}
                      </p>
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${st.cls}`}>{st.label}</span>
                </div>
              );
            })
          )}
        </div>

        {/* BP3 — Trailers & attached units (never consume the vehicle quota) */}
        <div className="mt-8 bg-white rounded-xl shadow border border-gray-100 p-6" data-testid="attached-units-section">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <div>
              <h2 className="font-semibold text-gray-900">Trailers &amp; attached units</h2>
              <p className="text-xs text-gray-500">Coupled to a vehicle — these do NOT count against your vehicle quota.</p>
            </div>
            <button
              onClick={() => setShowTrailerForm(!showTrailerForm)}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-cyan-600 text-white rounded-lg hover:bg-cyan-700"
            >
              {showTrailerForm ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
              {showTrailerForm ? 'Close' : 'Add attached unit'}
            </button>
          </div>

          {showTrailerForm && (
            <div className="mb-4 grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="trailer-unit">Unit number *</label>
                <input id="trailer-unit" value={trailerForm.unitNumber} onChange={(e) => setTrailerForm({ ...trailerForm, unitNumber: e.target.value })}
                  placeholder="e.g. TR-9" className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="trailer-type">Type *</label>
                <select id="trailer-type" value={trailerForm.unitType} onChange={(e) => setTrailerForm({ ...trailerForm, unitType: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                  {ATTACHED_UNIT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="trailer-owner">Coupled to vehicle *</label>
                <select id="trailer-owner" value={trailerForm.attachedTo} onChange={(e) => setTrailerForm({ ...trailerForm, attachedTo: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                  <option value="">Select a vehicle…</option>
                  {vehicles.map((v) => <option key={v.id} value={v.id}>{v.unitNumber}</option>)}
                </select>
                {vehicles.length === 0 && (
                  <p className="mt-1 text-xs text-amber-700">Add a vehicle first — an attached unit must couple to one of your vehicles.</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="trailer-label">Label</label>
                <input id="trailer-label" value={trailerForm.label} onChange={(e) => setTrailerForm({ ...trailerForm, label: e.target.value })}
                  placeholder="e.g. Tool trailer" className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div className="sm:col-span-2">
                <button onClick={addTrailer} disabled={saving || !trailerForm.unitNumber.trim() || !trailerForm.attachedTo}
                  className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50">
                  {saving ? 'Adding…' : 'Add attached unit'}
                </button>
              </div>
            </div>
          )}

          {units.length === 0 ? (
            <p className="text-sm text-gray-500">No trailers or attached units yet. Import them with a fleet CSV (kind=TRAILER) or add one here.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {units.map((u) => (
                <li key={u.id} className="py-2 flex items-center justify-between text-sm">
                  <span className="font-medium text-gray-900">{u.unitNumber} <span className="text-gray-500 font-normal">· {u.unitType}{u.label ? ` · ${u.label}` : ''} · coupled to {vehicles.find((v) => v.id === u.ownerId)?.unitNumber ?? `#${u.ownerId}`}</span></span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* BP3 — Drivers & vehicle assignment (existing DOT admin APIs, now surfaced) */}
        <div className="mt-6 bg-white rounded-xl shadow border border-gray-100 p-6" data-testid="drivers-section">
          <div className="flex items-center gap-2 mb-1">
            <UserCog className="h-5 w-5 text-cyan-600" />
            <h2 className="font-semibold text-gray-900">Drivers &amp; assignment</h2>
          </div>
          <p className="text-xs text-gray-500 mb-4">Designate a driver from your team, then assign a vehicle and shift date — the driver's eDVIR flow uses this assignment.</p>

          <div className="grid sm:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="driver-user">Designate driver *</label>
              <select id="driver-user" value={driverForm.userId} onChange={(e) => setDriverForm({ ...driverForm, userId: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                <option value="">Select a person…</option>
                {teamUsers.map((u) => <option key={u.id} value={u.id}>{u.firstName} {u.lastName}</option>)}
              </select>
              {teamUsers.length === 0 && (
                <p className="mt-1 text-xs text-amber-700">No people to pick from yet — import your team first on the Team page.</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="driver-cdl">CDL class</label>
              <select id="driver-cdl" value={driverForm.cdlClass} onChange={(e) => setDriverForm({ ...driverForm, cdlClass: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                {CDL_CLASSES.map((c) => <option key={c} value={c}>{c === 'NONE' ? 'None' : `Class ${c}`}</option>)}
              </select>
            </div>
            <div className="flex items-end">
              <button onClick={designateDriver} disabled={saving || !driverForm.userId}
                className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50">
                Designate
              </button>
            </div>
          </div>

          <div className="grid sm:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="assign-vehicle">Vehicle *</label>
              <select id="assign-vehicle" value={assignForm.vehicleId} onChange={(e) => setAssignForm({ ...assignForm, vehicleId: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                <option value="">Select…</option>
                {vehicles.map((v) => <option key={v.id} value={v.id}>{v.unitNumber}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="assign-driver">Driver *</label>
              <select id="assign-driver" value={assignForm.driverId} onChange={(e) => setAssignForm({ ...assignForm, driverId: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                <option value="">Select…</option>
                {drivers.map((d) => <option key={d.id} value={d.id}>{userName(d.userId)}</option>)}
              </select>
              {drivers.length === 0 && (
                <p className="mt-1 text-xs text-amber-700">No designated drivers yet — designate someone above first.</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="assign-date">Shift date *</label>
              <input id="assign-date" type="date" value={assignForm.shiftDate} onChange={(e) => setAssignForm({ ...assignForm, shiftDate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
            </div>
            <div className="flex items-end">
              <button onClick={createAssignment} disabled={saving || !assignForm.vehicleId || !assignForm.driverId || !assignForm.shiftDate}
                className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50">
                Assign
              </button>
            </div>
          </div>

          {drivers.length > 0 && (
            <ul className="mt-4 divide-y divide-gray-100 text-sm">
              {drivers.map((d) => (
                <li key={d.id} className="py-2">
                  <span className="font-medium text-gray-900">{userName(d.userId)}</span>
                  <span className="text-gray-500"> · CDL {d.cdlClass === 'NONE' ? 'none' : `Class ${d.cdlClass}`} · {d.active ? 'active' : 'inactive'}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
